import { useEffect, useRef, useState } from 'react';
import type { ComponentType, MouseEvent, SVGProps } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, LogIn, Swords, Trophy, X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useHaptics } from '@/hooks/useHaptics';
import { useMainCta } from '@/contexts/mainCta';
import {
  dismissActive,
  handleActive,
  promoteNext,
  selectActiveNudge,
  selectQueueLength,
} from '@/store/engagement.slice';
import {
  NUDGE_AUTO_DISMISS_MS,
  NUDGE_SLIDE_MS,
  defaultPromptBody,
  defaultPromptTitle,
} from '@/lib/engagement';
import { cn } from '@/lib/cn';

const ICON_BY_KIND: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  register: Trophy,
  checkin: LogIn,
  play: Swords,
};

/**
 * App-wide engagement nudge — a prominent full-width pill that slides up just
 * above the tab bar carrying one actionable prompt (rich: icon + title + body).
 * Auto-dismisses after a few seconds; tapping navigates to the target (and
 * marks the inbox copy read). Separate from the per-route `MainCta`, and
 * suppressed while a `MainCta` is showing. Anchored just above the tab bar
 * (whose Inbox tab is now the entry point the floating bell used to be).
 */
export function EngagementNudge() {
  const dispatch = useAppDispatch();
  const active = useAppSelector(selectActiveNudge);
  const queueLength = useAppSelector(selectQueueLength);
  const { cta } = useMainCta();
  const navigate = useNavigate();
  const location = useLocation();
  const haptics = useHaptics();
  const { t } = useTranslation('general');

  const [isOpen, setIsOpen] = useState(false);
  const dismissTimer = useRef<number | null>(null);

  const suppressed = cta != null;

  // Promote the next queued prompt when idle (and not suppressed by a MainCta).
  useEffect(() => {
    if (!suppressed && active == null && queueLength > 0) {
      dispatch(promoteNext());
    }
  }, [suppressed, active, queueLength, dispatch]);

  const activeId = active?.id;
  const activeTarget = active?.target;
  const onTargetRoute = activeTarget != null && location.pathname === activeTarget;

  // Slide-in + auto-dismiss whenever a new prompt becomes active. Skip prompts
  // whose target is the current route (you're already there).
  useEffect(() => {
    if (activeId == null) return;
    if (onTargetRoute) {
      dispatch(dismissActive());
      return;
    }
    const openId = window.setTimeout(() => setIsOpen(true), 16);
    const closeId = window.setTimeout(() => {
      setIsOpen(false);
      window.setTimeout(() => dispatch(dismissActive()), NUDGE_SLIDE_MS);
    }, NUDGE_AUTO_DISMISS_MS);
    dismissTimer.current = closeId;
    return () => {
      window.clearTimeout(openId);
      window.clearTimeout(closeId);
    };
  }, [activeId, onTargetRoute, dispatch]);

  if (suppressed || active == null || onTargetRoute) return null;

  const Icon = ICON_BY_KIND[active.kind] ?? Trophy;
  const title = t(active.titleKey, {
    defaultValue: defaultPromptTitle(active.kind),
    ...active.params,
  });
  const body = active.bodyKey
    ? t(active.bodyKey, { defaultValue: defaultPromptBody(active.kind), ...active.params })
    : '';

  const clearTimer = () => {
    if (dismissTimer.current != null) window.clearTimeout(dismissTimer.current);
  };

  const onTap = () => {
    haptics.impact('light');
    clearTimer();
    dispatch(handleActive());
    navigate(active.target);
  };

  const onDismiss = (e: MouseEvent) => {
    e.stopPropagation();
    haptics.selection();
    clearTimer();
    setIsOpen(false);
    window.setTimeout(() => dispatch(dismissActive()), NUDGE_SLIDE_MS);
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 z-30 bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] px-4">
      <div
        className={cn(
          'pointer-events-auto flex items-center gap-3 rounded-2xl border border-brand/50 bg-brand/15 p-3.5',
          'shadow-[0_10px_30px_rgba(0,0,0,0.55)] backdrop-blur',
          'transition-transform duration-300 ease-out',
          isOpen ? 'translate-y-0' : 'translate-y-[260%]',
        )}
      >
        <button
          type="button"
          onClick={onTap}
          className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity active:opacity-80"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/25 text-brand">
            <Icon aria-hidden className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-bold text-fg">{title}</span>
            {body && <span className="truncate text-xs text-fg-muted">{body}</span>}
          </span>
          <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-brand" />
        </button>
        <Button
          variant="icona"
          size={28}
          onClick={onDismiss}
          aria-label={t('engagement.dismiss', { defaultValue: 'Dismiss' })}
        >
          <X aria-hidden className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
